<template>
  <div>
    <div class="form-title">
      <h4>Expense Against PCR</h4>
    </div>

    <form>
      <div class="flex flex-wrap items-start justify-between">
        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Project <span class="text-danger">*</span></h6>
          <multiselect
            v-validate="'required'"
            label="project_name"
            class="w-full"
            value="project_id"
            v-model="formData.project"
            name="Project"
            :options="projectList"
            :searchable="true"
            :allow-empty="false"
            open-direction="bottom"
            placeholder="Select"
            select-label=""
            deselect-label="Can't remove"
            autocomplete="off"
            @select="projectSelected"
          />
          <p class="error-msg text-sm">
            <span class="text-danger text-sm" v-show="errors.has('Project')">
              {{ errors.first("Project") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Amount Paid From <span class="text-danger">*</span></h6>
          <multiselect
            v-validate="'required'"
            label="alt_org_name"
            class="w-full"
            value="default_alt_org_id"
            v-model="formData.default_alt_org"
            name="Amount Paid From"
            :options="alternateOrg"
            :searchable="true"
            :allow-empty="false"
            open-direction="bottom"
            placeholder="Select"
            select-label=""
            deselect-label="Can't remove"
            autocomplete="off"
            @select="storePaidFrom"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Amount Paid From')"
            >
              {{ errors.first("Amount Paid From") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Vendor <span class="text-danger">*</span></h6>
          <multiselect
            v-validate="'required'"
            :disabled="!formData.project || !formData.default_alt_org"
            class="w-full"
            v-model="formData.vendor"
            label="vendor_name"
            track-by="vendor_id"
            :options="vendorList"
            name="Vendor Name"
            open-direction="bottom"
            placeholder="Type to search"
            selectLabel=""
            selectedLabel=""
            deselect-label=""
            :limit="3"
            :allow-empty="false"
            :internal-search="false"
            @search-change="vendorSearched"
            @select="vendorSelected"
            preserveSearch
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Vendor Name')"
            >
              {{ errors.first("Vendor Name") }}
            </span>
          </p>
          <p
            v-if="
              formData.project && formData.default_alt_org && formData.vendor
            "
          >
            <vs-chip title="PCR Amount" color="#123">
              PCR Amount: {{ vendorPCRInfo.user_pcr }}
            </vs-chip>
            <vs-chip title="PCR Return Amount" color="#00F">
              Return Amount: {{ vendorPCRInfo.user_return_pcr }}
            </vs-chip>
            <vs-chip title="Balance PCR" color="success">
              Balance: {{ vendorPCRInfo.user_balance_pcr }}
            </vs-chip>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Invoice Number <span class="text-danger">*</span></h6>
          <vs-input
            v-validate="'required'"
            autocomplete="off"
            type="text"
            name="Invoice Number"
            v-model="formData.invoice_number"
            class="w-full"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Invoice Number')"
            >
              {{ errors.first("Invoice Number") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Invoice Date <span class="text-danger">*</span></h6>
          <Datepicker
            v-validate="'required'"
            placeholder="From Date"
            format="dd-MM-yyyy"
            v-model="formData.invoice_date"
            name="Invoice Date"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Invoice Date')"
            >
              {{ errors.first("Invoice Date") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Payment Due Date <span class="text-danger">*</span></h6>
          <Datepicker
            v-validate="'required'"
            placeholder="To Date"
            v-model="formData.payment_due_date"
            format="dd-MM-yyyy"
            name="Payment Due Date"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Payment Due Date')"
            >
              {{ errors.first("Payment Due Date") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Category <span class="text-danger">*</span></h6>
          <multiselect
            v-validate="'required'"
            class="w-full"
            label="category_name"
            track-by="category_id"
            v-model="formData.category"
            name="Category Name"
            :options="categoryList"
            :allow-empty="false"
            :closeOnSelect="false"
            searchable
            preserveSearch
            hideSelected
            multiple
            open-direction="bottom"
            placeholder="Select"
            select-label=""
            deselect-label=""
            @select="categorySelected"
            @remove="categoryRemoved"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Category Name')"
            >
              {{ errors.first("Category Name") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Subcategory <span class="text-danger">*</span></h6>
          <multiselect
            v-validate="'required'"
            class="w-full"
            label="category_name"
            track-by="category_id"
            v-model="formData.subcategory"
            name="Subcategory"
            :options="subcategoryList"
            searchable
            hideSelected
            multiple
            :allow-empty="false"
            :closeOnSelect="false"
            open-direction="bottom"
            placeholder="Select"
            select-label=""
            deselect-label="Can't remove"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Subcategory')"
            >
              {{ errors.first("Subcategory") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Country <span class="text-danger">*</span></h6>
          <multiselect
            v-validate="'required'"
            name="Country"
            class="w-full"
            label="country_name"
            track-by="country_id"
            v-model="formData.country"
            :options="countryList"
            open-direction="bottom"
            placeholder="Select"
            select-label=""
            deselect-label="Can't remove"
          />
          <p class="error-msg text-sm">
            <span class="text-danger text-sm" v-show="errors.has('Country')">
              {{ errors.first("Country") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Mode of payment</h6>
          <multiselect
            v-validate="'required'"
            class="w-full"
            label="payment_mode_name"
            track-by="payment_mode_id"
            v-model="formData.payment_mode"
            name="Mode of payment"
            :options="paymentModeList"
            open-direction="bottom"
            placeholder="Select"
            select-label=""
            deselect-label="Can't remove"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Mode of payment')"
            >
              {{ errors.first("Mode of payment") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Priority</h6>
          <multiselect
            v-validate="'required'"
            class="w-full"
            label="priority_name"
            track-by="priority_id"
            v-model="formData.priority"
            name="Priority"
            :options="priorityList"
            open-direction="bottom"
            placeholder="Select"
            select-label=""
            deselect-label="Can't remove"
          />
          <p class="error-msg text-sm">
            <span class="text-danger text-sm" v-show="errors.has('Priority')">
              {{ errors.first("Priority") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Set Name</h6>
          <multiselect
            v-validate="'required'"
            name="Set Name"
            class="w-full"
            label="location_name"
            track-by="location_id"
            v-model="formData.shootLocation"
            :options="shootLocationList"
            searchable
            allow-empty
            open-direction="bottom"
            placeholder="Select"
            select-label=""
            deselect-label="Can't remove"
          />
          <p class="error-msg text-sm">
            <span class="text-danger text-sm" v-show="errors.has('Set Name')">
              {{ errors.first("Set Name") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Approvers</h6>
          <multiselect
            v-validate="'required'"
            label="user_name"
            class="w-full"
            track-by="user_id"
            v-model="formData.approvers"
            name="Approver Name"
            :options="ApproverList"
            open-direction="bottom"
            placeholder="Select"
            select-label=""
            deselect-label="Can't remove"
            :allow-empty="false"
            :closeOnSelect="false"
            searchable
            multiple
            hideSelected
          >
          </multiselect>
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Approver Name')"
            >
              {{ errors.first("Approver Name") }}
            </span>
          </p>
        </div>
      </div>

      <div class="mb-5" v-if="formData.subcategory">
        <table class="w-full border rounded-lg">
          <thead class="bg-primary">
            <tr>
              <th class="w-64">
                <h5 class="text-white font-semibold">Line Item</h5>
              </th>
              <th>
                <h5 class="text-white font-semibold">Vendor</h5>
              </th>
              <!-- <th>
                <h5 class="text-white font-semibold text-center">
                  Unit <span class="text-danger">*</span>
                </h5>
              </th>
              <th>
                <h5 class="text-white font-semibold text-center">
                  Rate <span class="text-danger">*</span>
                </h5>
              </th> -->
              <th class="w-32">
                <h5 class="text-white font-semibold text-center">
                  Amount <span class="text-danger">*</span>
                </h5>
              </th>
              <th class="w-64">
                <h5 class="text-white font-semibold text-center">
                  Description <span class="text-danger">*</span>
                </h5>
              </th>
            </tr>
          </thead>
          <tbody
            v-for="subCategory in formData.subcategory"
            :key="`lineItem-${subCategory.category_id}`"
          >
            <tr>
              <th class="border p-1">
                <h6>{{ subCategory.category_name }}</h6>
              </th>
              <!-- <td>
                <vs-input
                  autocomplete="off"
                  type="number"
                  class="w-full"
                  v-model="subCategory.unit"
                />
              </td>
              <td>
                <vs-input
                  autocomplete="off"
                  type="number"
                  class="w-full"
                  v-model="subCategory.rate"
                />
              </td> -->
              <td class="border p-1">
                <multiselect
                  class="w-full"
                  v-model="subCategory.vendor"
                  label="vendor_name"
                  track-by="vendor_id"
                  :options="vendorList"
                  name="Vendor Name"
                  open-direction="bottom"
                  placeholder="Type to search"
                  selectLabel=""
                  selectedLabel=""
                  deselect-label=""
                  :limit="3"
                  :allow-empty="false"
                  :internal-search="false"
                  preserveSearch
                  @search-change="vendorSearched"
                />
              </td>
              <td class="border p-1">
                <vs-input
                  autocomplete="off"
                  type="number"
                  class="w-full"
                  v-model="subCategory.amount"
                  @input="calculateAmounts"
                />
              </td>
              <td class="border p-1">
                <vs-input
                  autocomplete="off"
                  type="text"
                  class="w-full"
                  v-model="subCategory.description"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex flex-wrap items-start justify-between">
        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Amount</h6>
          <vs-input
            disabled
            v-validate="'required'"
            type="number"
            name="Amount"
            v-model="formData.amount"
            class="w-full"
          />
          <p class="error-msg text-sm">
            <span class="text-danger text-sm" v-show="errors.has('Amount')">
              {{ errors.first("Amount") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Tax <span class="text-danger">*</span></h6>
          <div class="flex flex-wrap items-baseline justify-center">
            <div class="w-1/2 pr-1">
              <multiselect
                v-validate="'required'"
                :disabled="!formData.amount"
                class="w-full"
                label="gst_name"
                track-by="gst_id"
                v-model="formData.gst"
                :options="gstList"
                :allow-empty="false"
                open-direction="bottom"
                placeholder="Select"
                select-label=""
                deselect-label=""
                @input="calculateAmounts"
              />
            </div>
            <div class="w-1/2">
              <vs-input
                :disabled="
                  !formData.gst || formData.gst.gst_name != 'Manual Amount'
                "
                class="w-full"
                type="number"
                name="GST"
                @input="calculateAmounts"
                v-model="formData.gst_amount"
              />
              <p class="error-msg text-sm"></p>
            </div>
          </div>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Total Amount</h6>
          <vs-input
            class="w-full"
            type="number"
            v-model="formData.total_amount"
            disabled
          />
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Comment <span class="text-danger">*</span></h6>
          <vs-textarea
            v-validate="'required'"
            class="w-full"
            name="Comment"
            v-model="formData.comment"
            rows="5"
          />
        </div>

        <div class="w-full sm:w-1/2 md:w-2/3 p-1">
          <h6>Upload Document <span class="text-danger">*</span></h6>
          <vue-dropzone
            class="rounded"
            id="dropzone"
            ref="myVueDropzoneFile"
            :options="dropzoneOptionsFiles"
            @vdropzone-success-multiple="vdropzoneSuccessFile"
            @vdropzone-removed-file="vremoved"
            @vdropzone-error-multiple="vdropzoneError"
            @vdropzone-complete-multiple="vdropzoneComplete"
            @vdropzone-added-files="vdropzoneFilesAdded"
            destroyDropzone
            duplicateCheck
          />
          <vs-button
            v-if="fileUpload.deleteButtonStatus"
            type="filled"
            @click.prevent="clearFile()"
            class="feather icon-trash mt-2"
          />
          <vs-button
            v-if="fileUpload.deleteButtonStatus"
            type="filled"
            @click.prevent="openCropper()"
            class="ml-4 mt-2"
          >
            Crop Images
          </vs-button>
        </div>
      </div>

      <div class="flex flex-wrap items-center justify-center mt-5">
        <vs-button
          class="w-full sm:w-48 sm:mr-3"
          type="filled"
          @click.prevent="submitForm"
        >
          Submit
        </vs-button>
        <vs-button class="w-full sm:w-48" color="warning">Cancel</vs-button>
      </div>
    </form>

    <!-- Image Cropper Start -->
    <vs-popup
      id="popmodalCrop"
      class="popmodalViewR"
      title="Crop Image"
      :active.sync="popupCropImg"
    >
      <div class="vx-row mt-5">
        <div
          class="vx-col"
          :key="imageKey"
          v-for="(image, imageKey) in allSelectedImages"
        >
          <img
            @click="getImageToCrop(image)"
            v-if="image.dataURL"
            :src="image.dataURL"
            width="150"
            height="150"
          />
        </div>
      </div>
      <!-- SUBMIT BUTTON -->
      <div class="vx-row mt-5">
        <div class="vx-col mx-auto">
          <vue-cropper
            :img-style="{ width: '400px', height: '300px' }"
            v-if="showCropper == true"
            ref="cropper"
            :src="selectedImageForCrop.dataURL"
          >
          </vue-cropper>
          <vs-button
            @click="getCropedImage"
            color="success"
            type="filled"
            class="mt-3"
            >Crop</vs-button
          >
        </div>
      </div>
    </vs-popup>
    <!-- Image Cropper End -->
  </div>
</template>

<script>
// Services
import CategoryService from "@/services/categoryService.js";
import ContractService from "@/services/ContractService.js";
import ExpenseService from "@/services/expenseService.js";
import MethodService from "@/services/methodService.js";
import UserService from "@/services/UserService.js";

// Components
import Datepicker from "vuejs-datepicker";
import vue2Dropzone from "vue2-dropzone";

// Others
import { BASEURL } from "@/config/index";
import constant from "@/helper/constant.js";
import PermissionVariables from "@/services/UserManagementVaraibles.js";
import { debounce, throttle } from "@/helper/utils.js";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

export default {
  components: { Datepicker, vueDropzone: vue2Dropzone },
  props: {
    alternateOrg: { type: Array, required: true },
    projectList: { type: Array, required: true },
    countryList: { type: Array, required: true },
    currencyList: { type: Array, required: true },
    shootLocationList: { type: Array, required: true },
  },
  data() {
    return {
      vendorPCRInfo: {
        user_balance_pcr: 0,
        user_pcr: 0,
        user_return_pcr: 0,
      },
      formData: {
        project: null,
        default_alt_org: null,
        vendor: null,
        invoice_number: null,
        invoice_date: null,
        payment_due_date: null,
        category: null,
        subcategory: null,
        country: null,
        payment_mode: null,
        priority: null,
        shootLocation: null,
        approvers: null,
        amount: null,
        gst: null,
        gst_amount: null,
        total_amount: null,
        comment: null,
      },
      vendorList: [],
      categoryList: [],
      subcategoryList: [],
      paymentModeList: [],
      priorityList: [],
      gstList: [],
      ApproverList: [],

      popupCropImg: false,
      showCropper: false,
      selectedImageForCrop: null,

      dropzoneOptionsFiles: {
        parallelUploads: 100,
        url: BASEURL + "/v2/multipleUploads?organization=",
        ...constant.DEFAULT_FILES,
        acceptedFiles:
          ".pdf,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp,.doc,.docx",
        headers: { "My-Awesome-Header": "header value" },
        uploadMultiple: true,
        maxFiles: 100,
        addRemoveLinks: true,
      },
      fileUpload: {
        file_url: "",
        deleteButtonStatus: false,
      },
      uploadedDocument: "-",
      selectedFilesUUID: [],
      allSelectedImages: [],
      uploadedDocumentArray: [],

      organization_id: localStorage.getItem("OrganizationID"),
      permissionPageAccessArry: localStorage.getItem("permissionPageAccess")
        ? localStorage.getItem("permissionPageAccess").split(",")
        : "",
      loginUserRole: window.localStorage.getItem("UserRole"),
      loggedinUserID: localStorage.getItem("user_id"),
    };
  },
  methods: {
    getVendorPCRInfo() {
      ContractService.getVendorPCRInfo({})
        .then((response) => {})
        .catch((error) => console.error(error.message));
    },
    getVendorList(search = "") {
      if (!search) return;
      this.vendorList = [];
      let payload = { search };

      if (this.formData.default_alt_org.default_alt_org_id == 0) {
        payload["listType"] = "tv";
      }

      ContractService.getContractVendorNew(payload)
        .then((response) => {
          this.searchingVendor = false;
          if (response.data.Status) {
            this.vendorList = response.data.data;
          }
        })
        .catch((error) => {
          this.searchingVendor = false;
          console.error("Error : ", error.message);
        });
    },

    getMasterList: function() {
      MethodService.getAllMaster()
        .then((response) => {
          const { data } = response;
          if (!response.data.Status) {
            return;
          }

          this.priorityList = response.data.data.priority;
          if (this.priorityList.length > 0) {
            this.formData.priority = this.priorityList[0];
          }

          this.paymentModeList = response.data.data.payment_mode;
          if (this.paymentModeList.length > 0) {
            this.formData.payment_mode = this.paymentModeList[0];
          }
          this.gstList = response.data.data.gst;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    getCategoryList(project_id) {
      CategoryService.getProjectMainCategories(project_id)
        .then((response) => {
          if (response.data.Status) {
            this.categoryList = response.data.data;
            // if (this.form.category && this.form.category.category_id) {
            //   this.getSubcategoryList(
            //     this.form.category.category_id,
            //     project_id
            //   );
            // }
          }
        })
        .catch((error) => {
          console.error("Error => ", error.message);
        });
    },

    getSubcategoryList: function(category_id, project_id) {
      CategoryService.getSubCategoriesByParentCategory(category_id, project_id)
        .then((response) => {
          const { data } = response;
          if (data["data"] && data.Status) {
            this.subcategoryList = this.subcategoryList.concat(data.data);
          }
        })
        .catch((error) => {
          console.error("Error => ", error.message);
        });
    },

    getApproverList: function(id, user_id) {
      let payload = {
        id: id,
        user_id: user_id,
      };
      UserService.getApproverList(payload)
        .then((response) => {
          const { data } = response;
          if (data.Status) {
            this.ApproverList = data.data;
            //form.approver_id

            let rishabId = this.ApproverList.filter((x) => {
              return x.user_id == 55;
            });
            if (rishabId.length > 0) {
              this.form.approver_id = rishabId;
              // const itemToBeRemoved = rishabId;
              let index = this.ApproverList.findIndex(
                (x) => x.default_approver == "1"
              );
              this.ApproverList.splice(index, 1);
            }
          }
        })
        .catch((error) => {
          console.log("error :", error);
        });
    },

    projectSelected(project) {
      this.getCategoryList(project.project_id);
      this.getApproverList(project.project_id, this.loggedinUserID);
    },

    storePaidFrom() {
      this.vendorList = [];
      this.formData.vendor = null;
    },

    vendorSelected(selectedOption) {
      ContractService.getVendorPCRInfo({
        vendor_id: selectedOption.vendor_id,
        project_id: this.formData.project
          ? this.formData.project.project_id
          : 0,
      })
        .then((response) => {
          this.vendorPCRInfo = response.data.data;
          console.log("response => ", response);
        })
        .catch((error) => console.error(error.message));
    },

    categorySelected(selectedOption) {
      this.getSubcategoryList(
        selectedOption.category_id,
        this.formData.project.project_id
      );
    },

    categoryRemoved(selectedOption) {
      this.formData.subcategory = this.formData.subcategory.filter(
        (item) => item.parent_id != selectedOption.category_id
      );
    },

    calculateAmounts() {
      this.formData.amount = this.formData.subcategory
        ? this.formData.subcategory.reduce((accumulator, currentValue) => {
            return Number(accumulator || 0) + Number(currentValue.amount || 0);
          }, 0)
        : 0;

      this.formData.gst_amount =
        this.formData.gst && this.formData.gst.gst_percentage > 0
          ? (this.formData.gst.gst_percentage * this.formData.amount) / 100
          : this.formData.gst && this.formData.gst.gst_percentage == 0
          ? 0
          : this.formData.gst_amount || 0;

      this.formData.total_amount =
        Number(this.formData.amount) + Number(this.formData.gst_amount);
    },

    vdropzoneSuccessFile: function(file, response) {
      this.uploadedDocumentArray.push(response.data.image_path);
      this.fileUpload.deleteButtonStatus = true;
      this.uploadedDocument = this.uploadedDocumentArray.toString();
      // this.$refs.myVueDropzoneFile.disable();
    },

    vremoved: function(file) {
      let uploaded = this.uploadedDocument.split(",");
      var a = this.selectedFilesUUID.indexOf(file.upload.uuid);

      if (a > -1) {
        uploaded.splice(a, 1);
        this.selectedFilesUUID.splice(a, 1);
        this.uploadedDocumentArray.splice(a, 1);
      }
      this.uploadedDocument = uploaded.toString();
      if (uploaded.length <= 0) {
        this.fileUpload.deleteButtonStatus = false;
        this.uploadedDocument = "-";
        this.uploadedDocumentArray = [];
        this.selectedFilesUUID = [];
      }
    },

    vdropzoneFilesAdded: function(file) {},

    dataURItoBlob(dataURI) {
      console.log(dataURI, "jj");
      var byteString = atob(dataURI.split(",")[1]);
      var ab = new ArrayBuffer(byteString.length);
      var ia = new Uint8Array(ab);
      for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], { type: "image/jpeg" });
    },

    getCropedImage: function() {
      let blob = this.$refs.cropper
        .getCroppedCanvas()
        .toDataURL("image/jpeg", 0.9);
      var newFile = this.dataURItoBlob(blob);
      // assign original filename
      newFile.name = this.selectedImageForCrop.name;
      this.vremoved(this.selectedImageForCrop);
      this.$refs.myVueDropzoneFile.removeFile(this.selectedImageForCrop);
      this.$refs.myVueDropzoneFile.addFile(newFile);
    },

    getImageToCrop: function(file) {
      this.selectedImageForCrop = null;
      this.showCropper = false;
      this.selectedImageForCrop = file;

      setTimeout(() => {
        this.showCropper = true;
      }, 500);
    },

    closeCropper: function() {
      this.popupCropImg = false;
    },

    openCropper: function() {
      this.popupCropImg = true;
    },

    clearFile: function() {
      this.$refs.myVueDropzoneFile.removeAllFiles();
      this.fileUpload.deleteButtonStatus = false;
      this.$refs.myVueDropzoneFile.enable();
      this.uploadedDocument = "-";
      this.uploadedDocumentArray = [];
      this.selectedFilesUUID = [];
      this.allSelectedImages = [];
    },

    vdropzoneComplete: function(response) {
      for (let i = 0; i < response.length; i++) {
        this.allSelectedImages.push(response[i]);
        this.selectedFilesUUID.push(response[i].upload.uuid);
      }
    },

    vdropzoneError: function(file, message, xhr) {},

    submitForm() {
      // let payload = { ...this.formData };
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$vs.loading();
          let payload = {
            project_id: this.formData.project
              ? this.formData.project.project_id
              : 0,
            po_name: this.formData.vendor
              ? this.formData.vendor.vendor_name
              : "",
            item_name: "-",
            company_name: "-",
            customFieldValues: [],
            category_id: this.formData.subcategory
              ? this.formData.subcategory
                  .map((subcat) => subcat.category_id)
                  .join()
              : 0,
            company_address: "-",
            price: this.formData.amount,
            currency: 100,
            sgst: "0",
            cgst: "0",
            total: this.formData.amount,
            purchase_image: this.uploadedDocument || "-",
            country_id: this.formData.country
              ? this.formData.country.country_id
              : 100,
            purchase_description: this.formData.comment,
            purchase_status: "0",
            approved_percentage: "0",
            purchase_active: "1",
            location_id: this.formData.shootLocation
              ? this.formData.shootLocation.location_id
              : 0,
            payment_mode: this.formData.payment_mode
              ? this.formData.payment_mode.payment_mode_id
              : 0,
            payment_mode_description: "-",
            priority: this.formData.priority
              ? this.formData.priority.priority_id
              : "",
            default_alt_org_id: this.formData.default_alt_org
              ? this.formData.default_alt_org.default_alt_org_id
              : "",
            purchase_type: "expense",
            approver_id: this.formData.approvers
              ? this.formData.approvers.map((appr) => appr.user_id).join()
              : 0,
            city_id: "-",
            onBehalfId: 0,
            onBehalfName: "",
            isAlreadyPaid: 0,
            vendorName: this.formData.vendor
              ? this.formData.vendor.vendor_name
              : "",
            vendor_id: this.formData.vendor
              ? this.formData.vendor.vendor_id
              : 0,
            invoice_number: this.formData.invoice_number,
            payment_due_date: this.formData.payment_due_date,
            invoice_date: this.formData.invoice_date,
            gst_id: this.formData.gst ? this.formData.gst.gst_id : 0,
            gst_amount: this.formData.gst ? this.formData.gst_amount : 0,
            total_amount: this.formData.total_amount,
            raised_from_var: 0,
            is_foreign_remittance: 0,
            is_var: 6,
            budget_id: 0,
            purchase_lineitems: this.formData.subcategory
              ? this.formData.subcategory.map((item) => ({
                  category_id: item.category_id,
                  vendor_id: item.vendor ? item.vendor.vendor_id : null,
                  amount: item.amount,
                  description: item.description,
                }))
              : 0,
          };
          console.log("payload => ", payload);

          ExpenseService.addAdvanceExpense(payload)
            .then((response) => {
              const { data } = response;
              this.$vs.loading.close();
              if (!data.Status)
                return this.$vs.notify({
                  title: "Warning!",
                  text: data.Message,
                  iconPack: "feather",
                  icon: "check_box",
                  color: "warning",
                });

              this.clearForm();
              this.$validator.reset();
              return this.$vs.notify({
                title: "Created!",
                text: data.Message,
                iconPack: "feather",
                icon: "check_box",
                color: "success",
              });
            })
            .catch((error) => {
              this.$vs.notify({
                title: "Error!",
                text: error.message,
                iconPack: "feather",
                icon: "check_box",
                color: "warning",
              });
            });
        }
      });
    },

    clearForm() {
      this.formData = {
        project: null,
        default_alt_org: null,
        vendor: null,
        invoice_number: null,
        invoice_date: null,
        payment_due_date: null,
        category: null,
        subcategory: null,
        country: null,
        payment_mode: null,
        priority: null,
        shootLocation: null,
        amount: null,
        gst: null,
        gst_amount: null,
        total_amount: null,
        comment: null,
      };
      this.clearFile();
    },
  },
  created() {
    this.vendorSearched = debounce((event) => {
      this.getVendorList(event);
    }, 1000);
  },
  beforeMount() {
    this.getMasterList();
  },
};
</script>
